import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDBodyText,
  FDContentContainer,
  FDEmphasisSurface,
  FDHeadlineText,
  FDStack,
} from '@facet-wealth/design';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import PageHeader from 'Components/ComponentLibrary/Molecules/PageHeader';
import {
  useModalContextSubmissionSuccessful, useRefreshModalCollectionCleared, useRefreshModalItemDeleted,
} from 'Hooks/useRefreshEvent';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { CashFlowSectionPicker } from 'Views/Page/Content/Templates/CashFlowPageTemplate/CashFlowSectionPicker';
import { CompleteActionItemBanner } from 'Components/ComponentLibrary/Organisms/CompleteActionItemBanner';
import fr from 'facet-rest';
import { BigFacEnergy } from 'Components/ComponentLibrary/Atoms/Ornament/BigFacEnergy';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs } from 'Helpers/Testing';

export const CashFlowPageTemplate = ({ resource }) => {
  const { loadModalCollectionByLink, clearModalCollection } = useModalContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const isMobile = useMobileLayout();

  // If there's a getStarted relationship, open it immediately on load
  const [openedGetStarted, setOpenedGetStarted] = useState(false);
  useEffect(() => {
    resource.withRelationship(
      fr.relationships.getStarted.name,
      (rel) => {
        if (openedGetStarted) {
          return; // Don't open this on each re-render, only once
        }
        setOpenedGetStarted(true);
        loadModalCollectionByLink(rel.link('related').url());
      },
      () => {},
    );
  });

  useBoneBackgroundColor();
  useModalContextSubmissionSuccessful((evt, clearModalCollectionDependency) => {
    switch (evt.detail.response.meta().type()) {
      case 'IncomeDetail':
        clearModalCollectionDependency();
        break;
      case 'NoopFinishStep':
      case 'householdExpenseCategoryPX':
        refreshPageContentCollection();
        break;
      /* no default */
    }
  }, [clearModalCollection, refreshPageContentCollection]);

  useRefreshModalCollectionCleared((evt, refreshPageContentCollectionDependency) => {
    // In general, if you close a modal, we should refresh to show what you just entered.
    // However, when you complete the fillInDebts item, we redirect you to the dashboard - in that situation, we shouldn't refresh this page
    if (evt.detail.modalCollection.meta().type() !== 'ClientDashboardPage') {
      refreshPageContentCollectionDependency();
    }
  }, [refreshPageContentCollection]);

  useRefreshModalItemDeleted((_) => {
    refreshPageContentCollection();
  }, [refreshPageContentCollection]);

  useEffect(() => {
    resource.withRelationship(fr.relationships.actionLink.name,
      (rel) => loadModalCollectionByLink(rel.link('related').url()),
      () => null);
  }, []);

  const allSections = resource.attr('sections').resources().list();
  const monthlySurplus = allSections.find(sectionResource => sectionResource.attr('sectionName') === 'monthlySurplus');
  const normalSections = allSections.filter(sectionResource => sectionResource.attr('sectionName') !== 'monthlySurplus');

  const oneSectionVisible = normalSections.length === 1 && !monthlySurplus;

  return (
    <>
      <CompleteActionItemBanner resource={resource} />
      <FDContentContainer
        maxWidth={oneSectionVisible ? 'md' : 'xl'}
        {...dataAttrs('cashflow-page', resource)}
      >
        <PageHeader
          title={resource.attr('name')}
          subtitle={resource.attr('subtitle')}
          miniHero={monthlySurplus ? (
            <BigFacEnergy>
              <FDEmphasisSurface>
                <FDStack paddingX={3} paddingY={2} direction="column" alignItems="flex-end">
                  <FDBodyText size="sm" align="end" value={monthlySurplus.selectedOptionPrompt('sectionName')} />
                  <FDHeadlineText size="lg" align="end" value={monthlySurplus.attr('total')} data-test={`${monthlySurplus.attr('sectionName')}-total`} />
                  <FDBodyText size="sm" align="end" value={monthlySurplus.attr('description')} />
                </FDStack>
              </FDEmphasisSurface>
            </BigFacEnergy>
          ) : null}
        />
        <FDStack direction={isMobile ? 'column' : 'row'} spacing={8} justifyContent={'center'} alignItems={'flex-start'}>
          {normalSections.map((sectionResource) => (
            <CashFlowSectionPicker
              hasMaxWidth={!oneSectionVisible}
              key={sectionResource.id()}
              sectionResource={sectionResource}
              onEditItemClick={(rel) => loadModalCollectionByLink(rel.link('related').url())}
            />
          ))}
        </FDStack>
      </FDContentContainer>
    </>
  );
};

CashFlowPageTemplate.defaultProps = {
};

CashFlowPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};
